import { useEffect, useState, useCallback } from 'react';
import {
  getTimeOffRequests,
  actOnTimeOffRequest,
  getSickLeaves,
} from '../api/scheduleApi';
import TimeOffTotals from './TimeOffTotals';
import dayjs from 'dayjs';
import clsx from 'clsx';

export default function TimeOffView({ businessId }) {
  const [requests, setRequests] = useState([]);
  const [sickLeaves, setSick]   = useState([]);
  const [loading, setLoading]   = useState(true);

  /* ───── helpers ───── */
  const fmt = (iso) => dayjs(iso).format('DD MMM YYYY');
  const statusColours = {
    PENDING : 'bg-yellow-100 text-yellow-800',
    APPROVED: 'bg-green-100  text-green-800',
    REJECTED: 'bg-red-100    text-red-800',
  };

  /* ───── load data ───── */
  const load = useCallback(async () => {
    setLoading(true);
    const [to, sick] = await Promise.all([
      getTimeOffRequests(businessId),
      getSickLeaves(businessId),
    ]);

    /* keep anything that hasn’t finished before today */
    const today = dayjs().startOf('day');
    const upcoming = to.filter((r) =>
      !dayjs(r.end_date ?? r.start_date).isBefore(today, 'day')
    );

    setRequests(upcoming);
    setSick(sick);
    setLoading(false);
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  /* ───── actions ───── */
  const decide = async (id, status) => {
    await actOnTimeOffRequest(businessId, id, status);
    load();                                   // refresh after a change
  };

  /* ───── ui ───── */
  if (loading) return <p>Loading …</p>;

  return (
    <div className="space-y-8">
      {/* ─── upcoming list ─── */}
      <section>
        <h2 className="font-medium mb-2">Upcoming Time-Off</h2>
        {requests.length === 0 ? (
          <p className="text-sm text-gray-500">None 🎉</p>
        ) : (
          <ul className="divide-y">
            {requests.map((r) => (
              <li
                key={r.id}
                className="py-2 flex flex-wrap justify-between items-center space-y-1 sm:space-y-0"
              >
                <span className="flex-1">
                  <strong>{r.employee?.full_name}</strong> – {fmt(r.start_date)}
                  {r.end_date && ` → ${fmt(r.end_date)}`}
                  {r.reason && ` (${r.reason})`}
                  <span
                    className={clsx(
                      'ml-2 px-2 py-0.5 rounded-full text-xs uppercase font-semibold',
                      statusColours[r.status]
                    )}
                  >
                    {r.status}
                  </span>
                </span>

                <span className="space-x-2 shrink-0">
                  <button
                    className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 rounded"
                    onClick={() => decide(r.id, 'REJECTED')}
                  >
                    Reject
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-green-100 hover:bg-green-200 rounded"
                    onClick={() => decide(r.id, 'APPROVED')}
                  >
                    Approve
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ─── sick-leave totals ─── */}
      <TimeOffTotals sickLeaves={sickLeaves} />
    </div>
  );
}
